// MobileTabBar — bottom navigation for the mobile shell (native iOS app or
// narrow browser viewport). Additive: the NavSidebar drawer (hamburger)
// still reaches every route; the tab bar surfaces only the views a phone
// earns (roadmap docs/MOBILE-IOS-ROADMAP.md Phase 3).
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Building2, BarChart3, CheckSquare, LayoutDashboard } from 'lucide-react';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { tapHaptic } from '../../platform';
import './MobileTabBar.css';

const TABS = [
  { key: 'home', label: 'Home', path: '/home', icon: Home, show: () => true },
  { key: 'accounts', label: 'Accounts', path: '/accounts', icon: Building2, show: ({ isAdmin }) => isAdmin },
  { key: 'vendors', label: 'Vendors', path: '/vendors', icon: BarChart3, show: ({ isFeatureEnabled }) => isFeatureEnabled('vendor_analysis') },
  { key: 'tasks', label: 'Tasks', path: '/tasks', icon: CheckSquare, show: ({ isAdmin }) => isAdmin },
  // Non-admins don't have /workspace and /tasks; give them the dashboard.
  { key: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, show: ({ isAdmin }) => !isAdmin },
];

export default function MobileTabBar({ isAdmin, isCustomer, isFeatureEnabled, hidden = false }) {
  const isMobileShell = useIsMobileShell();
  const location = useLocation();
  const navigate = useNavigate();

  // Customers only have /zero-trust — a one-item tab bar is noise.
  if (!isMobileShell || isCustomer || hidden) return null;

  const tabs = TABS.filter((t) => t.show({ isAdmin, isFeatureEnabled }));

  const handleTap = (tab) => {
    tapHaptic();
    navigate(tab.path);
  };

  return (
    <nav className="mobile-tab-bar" aria-label="Primary">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const active = location.pathname.startsWith(tab.path);
        return (
          <button
            key={tab.key}
            type="button"
            className={`mobile-tab ${active ? 'mobile-tab-active' : ''}`}
            aria-current={active ? 'page' : undefined}
            onClick={() => handleTap(tab)}
          >
            <Icon size={22} strokeWidth={active ? 2.4 : 1.8} aria-hidden="true" />
            <span className="mobile-tab-label">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
